import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton,
  IonButton, IonIcon, IonSpinner, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonList, IonItem, IonLabel,
} from '@ionic/react';
import { playOutline, pauseOutline, sparklesOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { api, apiErrorMessage } from '@/lib/api';
import { asArray } from '@/lib/collections';
import { useCampaignStore } from '@/store/campaigns.store';
import { toast } from '@/store/ui.store';
import StatusPill from '@/components/StatusPill';
import PlatformChip from '@/components/PlatformChip';

interface CampaignPost {
  id: string;
  title: string | null;
  status: string;
  platform_ids: string[];
  publish_at: string | null;
}

export default function CampaignDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const publish = useCampaignStore((s) => s.publish);
  const pause = useCampaignStore((s) => s.pause);
  const [campaign, setCampaign] = useState<Record<string, unknown> | null>(null);
  const [posts, setPosts] = useState<CampaignPost[]>([]);
  const [insights, setInsights] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    try {
      const [detail, postList] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get<{ data: CampaignPost[] }>(`/campaigns/${id}/posts`),
      ]);
      setCampaign(detail.data as Record<string, unknown>);
      setPosts(asArray(postList.data?.data));
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runInsights = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const { data } = await api.post<{ insights: string[] }>('/campaigns/insights', { campaignId: id });
      setInsights(asArray(data?.insights));
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (!campaign) {
    return (
      <IonPage>
        <IonContent><div className="empty-state"><IonSpinner /></div></IonContent>
      </IonPage>
    );
  }

  const status = String(campaign.status ?? 'planning');
  const planItems = asArray((campaign.ai_plan as { items?: Array<Record<string, unknown>> } | null)?.items);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/app/campaigns" /></IonButtons>
          <IonTitle>{String(campaign.name ?? '')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <StatusPill status={status} />
            {(campaign.platform_ids as string[] | undefined)?.map?.((platform) => (
              <PlatformChip key={platform} platform={platform as never} />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
            {status === 'planning' && (
              <IonButton onClick={() => { void publish(id!); }}>
                <IonIcon slot="start" icon={playOutline} />
                {t('campaigns.publishCampaign')}
              </IonButton>
            )}
            {status === 'active' && (
              <IonButton fill="outline" onClick={() => { void pause(id!); }}>
                <IonIcon slot="start" icon={pauseOutline} />
                {t('campaigns.stopCampaign')}
              </IonButton>
            )}
            <IonButton fill="outline" disabled={busy} onClick={() => void runInsights()}>
              {busy ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={sparklesOutline} />}
              {t('campaigns.insights')}
            </IonButton>
          </div>

          {insights && (
            <IonCard className="post-card">
              <IonCardHeader><IonCardTitle style={{ fontSize: '1rem' }}>{t('campaigns.insights')}</IonCardTitle></IonCardHeader>
              <IonCardContent>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {insights.map((insight, index) => <li key={index} style={{ marginBottom: 6 }}>{insight}</li>)}
                </ul>
              </IonCardContent>
            </IonCard>
          )}

          <h6 className="section-title">{t('campaigns.posts')}</h6>
          {posts.length === 0 ? (
            <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>{t('campaigns.insightsEmpty')}</p>
          ) : (
            <IonList inset>
              {posts.map((post) => (
                <IonItem key={post.id}>
                  <div slot="start"><StatusPill status={post.status} /></div>
                  <IonLabel>
                    <h3>{post.title ?? '—'}</h3>
                    <p>{post.publish_at ? new Date(post.publish_at).toLocaleString() : '—'}</p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          )}

          {planItems.length > 0 && (
            <>
              <h6 className="section-title">AI plan</h6>
              <IonList inset>
                {planItems.map((item, index) => (
                  <IonItem key={index}>
                    <IonLabel>
                      <h3>{String(item.title ?? '')}</h3>
                      <p>{String(item.publishAt ?? '')}</p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
